import { memo, useEffect, useRef, useState } from 'react';
import { APIProvider, Map as GoogleMap, AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapErrorBoundary } from './MapErrorBoundary';

/**
 * A request to move the camera. `token` changes on every new request so the
 * controller re-fires even when coords repeat. When `lat`/`lng` are present the
 * camera pans straight there; when only `query` is present (the selected
 * location has no stored coords) it is geocoded for a precise pin. `zoom` drives
 * the progressive zoom-in as deeper location levels are picked.
 */
export interface MapFocus {
  lat?: number | null;
  lng?: number | null;
  query?: string | null;
  zoom: number;
  token: number;
}

interface Props {
  lat?: string;
  lng?: string;
  onCoordinatesChange: (lat: string, lng: string) => void;
  height?: number;
  focus?: MapFocus | null;
}

/**
 * Drives camera movement (pan + progressive zoom) and name→coords geocoding.
 * Rendered inside <Map> so useMap() resolves the map instance. Returns null —
 * it only runs side effects.
 */
function MapFocusController({
  focus,
  onResolve,
}: {
  focus?: MapFocus | null;
  onResolve: (lat: string, lng: string) => void;
}) {
  const map = useMap();
  const geocodingLib = useMapsLibrary('geocoding');
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const lastToken = useRef<number | null>(null);

  if (geocodingLib && !geocoderRef.current) {
    geocoderRef.current = new geocodingLib.Geocoder();
  }

  useEffect(() => {
    if (!map || !focus || focus.token === lastToken.current) return;
    lastToken.current = focus.token;

    const apply = (lat: number, lng: number) => {
      map.panTo({ lat, lng });
      map.setZoom(focus.zoom);
    };

    const hasCoords =
      focus.lat != null && focus.lng != null &&
      Number.isFinite(focus.lat) && Number.isFinite(focus.lng);

    // Move to the best coords we already have (selected row or ancestor) so the
    // camera responds immediately, even before/without geocoding.
    if (hasCoords) apply(focus.lat as number, focus.lng as number);

    // No stored coords for the selected level → geocode its name for a precise
    // pin. Restricted to Guatemala. Refines the pin (and form coords) on success;
    // on failure we keep whatever `hasCoords` already placed.
    if (focus.query && geocoderRef.current) {
      geocoderRef.current.geocode(
        { address: focus.query, componentRestrictions: { country: 'GT' } },
        (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location;
            const lat = loc.lat();
            const lng = loc.lng();
            onResolve(String(lat), String(lng));
            apply(lat, lng);
          }
        },
      );
    }
  }, [map, focus, onResolve]);

  return null;
}

/**
 * Shared Google Map picker. Click the map to drop/move the marker, or drag the
 * marker to adjust. Fires `onCoordinatesChange(lat, lng)` on both interactions.
 * Pass `focus` to pan + progressively zoom as location levels are selected.
 * Default view centers on Guatemala City when no coordinates are provided.
 */
function LocationMapImpl({ lat, lng, onCoordinatesChange, height = 300, focus }: Props) {
  const defaultCenter = { lat: 14.6349, lng: -90.5069 };
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);

  useEffect(() => {
    // Only set a marker for genuinely VALID coords. A truthy-but-non-numeric
    // string (half-entered value, legacy "NaN") or an out-of-range/swapped
    // value (e.g. legacy rows with lat/lng transposed → lat 270) makes
    // AdvancedMarker throw "reading 'keys'" inside Google Maps. The
    // MapErrorBoundary contains the throw, but it still spams the tracker and
    // blanks the map — so reject anything outside the valid lat/lng domain up
    // front and just show the default view instead.
    const inRange = (n: number, max: number) => !Number.isNaN(n) && Math.abs(n) <= max;
    const latNum = lat ? parseFloat(lat) : NaN;
    const lngNum = lng ? parseFloat(lng) : NaN;
    setMarkerPosition(
      inRange(latNum, 90) && inRange(lngNum, 180) ? { lat: latNum, lng: lngNum } : null,
    );
  }, [lat, lng]);

  const handleMapClick = (e: any) => {
    if (e.detail.latLng) {
      const newLat = e.detail.latLng.lat.toString();
      const newLng = e.detail.latLng.lng.toString();
      onCoordinatesChange(newLat, newLng);
    }
  };

  const handleMarkerDragEnd = (e: any) => {
    if (e.latLng) {
      onCoordinatesChange(e.latLng.lat().toString(), e.latLng.lng().toString());
    }
  };

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

  const fallback = (
    <div
      style={{ width: '100%', height, borderRadius: 12 }}
      className="flex items-center justify-center bg-slate-100 dark:bg-white/5 text-center px-4"
    >
      <p className="text-xs text-slate-500">
        Mapa no disponible. Verifica la conexión o el dominio autorizado de Google Maps.
      </p>
    </div>
  );

  return (
    <MapErrorBoundary fallback={fallback}>
      <APIProvider apiKey={apiKey}>
        <div style={{ width: '100%', height, borderRadius: 12, overflow: 'hidden' }}>
          <GoogleMap
            defaultCenter={markerPosition || defaultCenter}
            defaultZoom={markerPosition ? 14 : 7}
            mapId="LOCATION_PICKER_MAP"
            onClick={handleMapClick}
            disableDefaultUI={true}
            gestureHandling="greedy"
          >
            <MapFocusController focus={focus} onResolve={onCoordinatesChange} />
            {markerPosition && (
              <AdvancedMarker
                position={markerPosition}
                draggable={true}
                onDragEnd={handleMarkerDragEnd}
              />
            )}
          </GoogleMap>
        </div>
      </APIProvider>
    </MapErrorBoundary>
  );
}

// Memoized so unrelated parent re-renders (e.g. the gallery upload progress
// ticking in AddPropertyView) don't churn the Google Map + AdvancedMarker,
// which can trip a marker-init race in Google's library. Requires callers to
// pass a STABLE onCoordinatesChange (useCallback) for the memo to hold; `focus`
// is held in parent state so it only changes when a new location is selected.
export const LocationMap = memo(LocationMapImpl);
